import { v4 as uuidv4 } from 'uuid';

interface StreamingState {
  clientRequestId: string;
  streamMessageId?: string;
  startTime: number;
  isActive: boolean;
  hasReceivedStart: boolean;
  hasReceivedEnd: boolean;
}

class StreamingStateTracker {
  private activeStreams: Map<string, StreamingState> = new Map();
  
  // Create a new tracking entry for an API request
  startRequest(): string {
    const clientRequestId = uuidv4();
    this.activeStreams.set(clientRequestId, {
      clientRequestId,
      startTime: Date.now(),
      isActive: true,
      hasReceivedStart: false,
      hasReceivedEnd: false
    });
    return clientRequestId;
  }
  
  // Update when we receive resumeInterviewStreamStart
  handleStreamStart(clientRequestId: string, streamMessageId: string) {
    const state = this.activeStreams.get(clientRequestId);
    if (state) {
      state.streamMessageId = streamMessageId;
      state.hasReceivedStart = true;
    }
  }
  
  // Update when we receive resumeInterviewStreamEnd
  handleStreamEnd(streamMessageId: string) {
    // Find the state by streamMessageId
    for (const [clientRequestId, state] of this.activeStreams.entries()) {
      if (state.streamMessageId === streamMessageId) {
        state.hasReceivedEnd = true;
        state.isActive = false;
        // Clean up after a delay
        setTimeout(() => {
          this.activeStreams.delete(clientRequestId);
        }, 5000);
        break;
      }
    }
  }
  
  // Check if a request has active streaming
  isStreamingActive(clientRequestId: string): boolean {
    const state = this.activeStreams.get(clientRequestId);
    return state ? state.isActive && state.hasReceivedStart : false;
  }
  
  
  // Clean up old entries (call periodically)
  cleanup() {
    const now = Date.now();
    const timeout = 5 * 60 * 1000; // 5 minutes
    
    for (const [clientRequestId, state] of this.activeStreams.entries()) {
      if (now - state.startTime > timeout) {
        this.activeStreams.delete(clientRequestId);
      }
    }
  }
  
  // Get state for debugging
  getState(clientRequestId: string): StreamingState | undefined {
    return this.activeStreams.get(clientRequestId);
  }
  
  // Find clientRequestId by streamMessageId
  findClientRequestId(streamMessageId: string): string | undefined {
    for (const [clientRequestId, state] of this.activeStreams.entries()) {
      if (state.streamMessageId === streamMessageId) {
        return clientRequestId;
      }
    }
    return undefined;
  }
}

export const streamingStateTracker = new StreamingStateTracker();